import React, {
    useState,
    useCallback,
    useRef,
    useLayoutEffect,
    useEffect,
} from 'react'
import { Dropdown, Popover } from 'antd'
import { Icon } from '@iconify/react'
import Picker from '@emoji-mart/react'
import emojiData from '@emoji-mart/data'
import styles from './MentionEditorInsertMenu.module.css'

const ICON_SIZE = 18
const MENU_ICON_STYLE = { color: '#757677', marginRight: 8, flexShrink: 0 }
const MENU_EDGE_GAP = 12
const MENU_IDEAL_MAX = 360
const MENU_MIN_HEIGHT = 160

const getCommentsFooterTop = (triggerEl) => {
    if (!triggerEl || typeof document === 'undefined') return null

    const root =
        triggerEl.closest('[class*="drawerContainer"]') ||
        triggerEl.closest('[class*="drawer"]') ||
        document.body

    const footer =
        root.querySelector('[class*="commentsFooter"]') ||
        root.querySelector('[class*="commentInputFooter"]')

    if (!footer) return null
    return footer.getBoundingClientRect().top
}

const getViewportMenuLayout = (triggerEl, preferredPlacement = 'topLeft') => {
    if (!triggerEl || typeof window === 'undefined') {
        return {
            placement: preferredPlacement,
            maxHeight: MENU_IDEAL_MAX,
        }
    }

    const rect = triggerEl.getBoundingClientRect()
    const spaceAbove = Math.max(0, rect.top - MENU_EDGE_GAP)
    const footerTop = getCommentsFooterTop(triggerEl)
    // Keep menu out of sticky comments footer when opening downward.
    const spaceBelowRaw =
        footerTop != null && footerTop > rect.bottom
            ? footerTop - rect.bottom - MENU_EDGE_GAP
            : window.innerHeight - rect.bottom - MENU_EDGE_GAP
    const spaceBelow = Math.max(0, spaceBelowRaw)

    const preferTop = String(preferredPlacement).toLowerCase().startsWith('top')
    // Description toolbar sits above comments — always open up unless almost no room.
    const openUp = preferTop
        ? spaceAbove >= 120 || spaceAbove >= spaceBelow
        : spaceBelow >= MENU_MIN_HEIGHT && spaceBelow > spaceAbove + 40

    const available = openUp ? spaceAbove : spaceBelow
    const maxHeight = Math.max(
        MENU_MIN_HEIGHT,
        Math.min(MENU_IDEAL_MAX, Math.floor(available))
    )

    const side = String(preferredPlacement).toLowerCase().endsWith('right')
        ? 'Right'
        : 'Left'

    return {
        placement: openUp ? `top${side}` : `bottom${side}`,
        maxHeight,
    }
}

/**
 * Shared insert dropdown (task context menu design).
 * @param {object} props
 * @param {Array} props.sections - `{ items: [{ key, label, icon }] }[]`
 * @param {(key: string) => boolean|void} props.onInsertItem - return `false` to keep menu open
 * @param {string} props.plusButtonClassName
 * @param {string} [props.wrapClassName]
 * @param {string} [props.buttonTitle]
 * @param {string} [props.placement]
 * @param {boolean} [props.showLinkPopover]
 * @param {object} [props.emoji] - `{ open, onOpenChange, onSelect, savedRangeRef }`
 * @param {object} [props.link] - `{ open, state, onStateChange, onSubmit, onClose, quillRef }`
 * @param {object} [props.image] - `{ inputRef, onChange }`
 */
const QuillInsertDropdown = ({
    sections,
    onInsertItem,
    plusButtonClassName,
    wrapClassName = '',
    buttonTitle = 'Insert',
    placement = 'topLeft',
    showLinkPopover = true,
    emoji,
    link,
    image,
}) => {
    const linkUrlInputRef = useRef(null)
    const linkTextInputRef = useRef(null)
    const emojiAnchorRef = useRef(null)
    const triggerRef = useRef(null)

    const [insertOpen, setInsertOpen] = useState(false)
    const [resolvedPlacement, setResolvedPlacement] = useState(placement)
    const [menuMaxHeight, setMenuMaxHeight] = useState(MENU_IDEAL_MAX)
    const lockedPlacementRef = useRef(null)

    const emojiOpen = emoji?.open ?? false

    const updateMenuLayout = useCallback(
        ({ lockPlacement = false } = {}) => {
            const layout = getViewportMenuLayout(triggerRef.current, placement)

            if (lockPlacement || !lockedPlacementRef.current) {
                lockedPlacementRef.current = layout.placement
                setResolvedPlacement(layout.placement)
            }

            const activePlacement =
                lockedPlacementRef.current || layout.placement
            const openUp = String(activePlacement)
                .toLowerCase()
                .startsWith('top')

            // While open, keep the same direction — only shrink height to stay on-screen.
            const rect = triggerRef.current?.getBoundingClientRect()
            let maxHeight = layout.maxHeight
            if (rect) {
                const footerTop = getCommentsFooterTop(triggerRef.current)
                if (openUp) {
                    maxHeight = Math.max(
                        MENU_MIN_HEIGHT,
                        Math.min(
                            MENU_IDEAL_MAX,
                            Math.floor(rect.top - MENU_EDGE_GAP)
                        )
                    )
                } else {
                    const limit =
                        footerTop != null && footerTop > rect.bottom
                            ? footerTop - rect.bottom - MENU_EDGE_GAP
                            : window.innerHeight - rect.bottom - MENU_EDGE_GAP
                    maxHeight = Math.max(
                        MENU_MIN_HEIGHT,
                        Math.min(MENU_IDEAL_MAX, Math.floor(limit))
                    )
                }
            }

            setMenuMaxHeight(maxHeight)
            setResolvedPlacement(activePlacement)
        },
        [placement]
    )

    const handleInsertOpenChange = useCallback(
        (open) => {
            if (open) {
                lockedPlacementRef.current = null
                updateMenuLayout({ lockPlacement: true })
            } else {
                lockedPlacementRef.current = null
            }
            setInsertOpen(open)
        },
        [updateMenuLayout]
    )

    useLayoutEffect(() => {
        if (!insertOpen) return undefined

        const handleReposition = () => updateMenuLayout({ lockPlacement: false })
        window.addEventListener('resize', handleReposition)
        // Close on scroll so the menu cannot drift over the comments footer.
        const handleScroll = (event) => {
            const target = event.target
            if (
                target instanceof Element &&
                target.closest(`.${styles.insertMenu}`)
            ) {
                return
            }
            setInsertOpen(false)
            lockedPlacementRef.current = null
        }
        window.addEventListener('scroll', handleScroll, true)

        return () => {
            window.removeEventListener('resize', handleReposition)
            window.removeEventListener('scroll', handleScroll, true)
        }
    }, [insertOpen, updateMenuLayout])

    const setEmojiOpen = useCallback(
        (open) => {
            if (open) emoji?.onOpenChange?.(true)
            else {
                emoji?.onOpenChange?.(false)
                emoji?.onClose?.()
            }
        },
        [emoji]
    )

    const linkPopup = link?.state ?? {
        open: false,
        text: '',
        url: '',
        savedRange: null,
    }
    const setLinkPopup = link?.onStateChange

    const closeInsertMenu = useCallback(() => setInsertOpen(false), [])

    const closeLinkPopup = useCallback(() => {
        link?.onClose?.()
        link?.quillRef?.current?.focus()
    }, [link])

    const handleLinkOpenFromInsert = useCallback(() => {
        const q = link?.quillRef?.current
        if (!q || !setLinkPopup) return
        const range = q.getSelection()
        const fmt = range ? q.getFormat(range) : {}
        const selectedText = range?.length
            ? q.getText(range.index, range.length)
            : ''
        closeInsertMenu()
        setLinkPopup({
            open: true,
            text: selectedText,
            url: fmt.link || '',
            savedRange: range,
        })
    }, [link, closeInsertMenu, setLinkPopup])

    const handleLinkSubmit = useCallback(() => {
        if (link?.onSubmit) {
            link.onSubmit()
            return
        }
        closeLinkPopup()
    }, [link, closeLinkPopup])

    const handleLinkKeyDown = useCallback(
        (e) => {
            if (e.key === 'Enter') {
                e.preventDefault()
                handleLinkSubmit()
            }
            if (e.key === 'Escape') {
                e.preventDefault()
                closeLinkPopup()
            }
        },
        [handleLinkSubmit, closeLinkPopup]
    )

    useLayoutEffect(() => {
        if (!linkPopup.open) return
        const focusTarget = linkPopup.text
            ? linkUrlInputRef.current
            : linkTextInputRef.current
        setTimeout(() => focusTarget?.focus(), 0)
        // Focus only when the popover opens; do not re-run when text/url change while typing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkPopup.open])

    // Close emoji picker when clicking the comment/editor (outside the picker)
    useEffect(() => {
        if (!emojiOpen) return undefined

        const handlePointerDown = (event) => {
            const target = event.target
            if (!(target instanceof Element)) return
            if (target.closest('em-emoji-picker')) return
            if (target.closest(`.${styles.emojiPopover}`)) return
            if (emojiAnchorRef.current?.contains(target)) return
            setEmojiOpen(false)
            closeInsertMenu()
        }

        document.addEventListener('mousedown', handlePointerDown, true)
        return () =>
            document.removeEventListener('mousedown', handlePointerDown, true)
    }, [emojiOpen, setEmojiOpen, closeInsertMenu])

    const handleEmojiSelect = useCallback(
        (emojiNative) => {
            emoji?.onSelect?.(emojiNative)
            setEmojiOpen(false)
            closeInsertMenu()
        },
        [emoji, setEmojiOpen, closeInsertMenu]
    )

    const handleItemClick = useCallback(
        (key) => {
            if (key === 'link' && showLinkPopover && link) {
                handleLinkOpenFromInsert()
                return
            }
            const keepOpen = onInsertItem(key)
            if (keepOpen !== false) {
                setInsertOpen(false)
            }
        },
        [onInsertItem, showLinkPopover, link, handleLinkOpenFromInsert]
    )

    const insertMenuContent = (
        <div
            className={styles.insertMenu}
            style={{ maxHeight: menuMaxHeight }}
        >
            <div className={styles.insertMenuLabel}>Insert</div>
            <div className={styles.insertMenuScroll}>
                {sections.map((section, sectionIndex) => (
                    <div
                        key={sectionIndex}
                        className={styles.insertMenuSection}
                    >
                        {section.items.map((item) => (
                            <button
                                key={item.key}
                                type="button"
                                className={styles.insertMenuItem}
                                title={item.label}
                                onMouseDown={(e) => {
                                    e.preventDefault()
                                    handleItemClick(item.key)
                                }}
                            >
                                <Icon
                                    icon={item.icon}
                                    width={16}
                                    height={16}
                                    style={MENU_ICON_STYLE}
                                />
                                <span>{item.label}</span>
                            </button>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    )

    const linkPopoverContent = showLinkPopover && link && setLinkPopup && (
        <div onMouseDown={(e) => e.stopPropagation()}>
            <input
                ref={linkTextInputRef}
                className={styles.linkInput}
                value={linkPopup.text}
                onChange={(e) =>
                    setLinkPopup((s) => ({ ...s, text: e.target.value }))
                }
                onKeyDown={handleLinkKeyDown}
                placeholder={link?.textPlaceholder ?? 'Text'}
                type="text"
            />
            <input
                ref={linkUrlInputRef}
                className={styles.linkInput}
                value={linkPopup.url}
                onChange={(e) =>
                    setLinkPopup((s) => ({ ...s, url: e.target.value }))
                }
                onKeyDown={handleLinkKeyDown}
                placeholder={link?.urlPlaceholder ?? 'URL'}
                type="url"
            />
            <div className={styles.linkActions}>
                <button
                    type="button"
                    className={styles.linkCancelBtn}
                    onMouseDown={(e) => {
                        e.preventDefault()
                        closeLinkPopup()
                    }}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className={styles.linkSubmitBtn}
                    onMouseDown={(e) => {
                        e.preventDefault()
                        handleLinkSubmit()
                    }}
                >
                    Apply
                </button>
            </div>
        </div>
    )

    const plusButton = (
        <button
            ref={triggerRef}
            type="button"
            className={plusButtonClassName}
            onMouseDown={(e) => e.preventDefault()}
            title={buttonTitle}
            aria-label={buttonTitle}
        >
            <Icon icon="mdi:plus" width={ICON_SIZE} height={ICON_SIZE} />
        </button>
    )

    const dropdown = (
        <Dropdown
            trigger={['click']}
            placement={resolvedPlacement}
            open={insertOpen}
            onOpenChange={handleInsertOpenChange}
            overlayClassName={styles.insertDropdown}
            popupRender={() => insertMenuContent}
            getPopupContainer={() => document.body}
            destroyOnHidden
            autoAdjustOverflow={false}
            align={{ overflow: { adjustX: true, adjustY: false } }}
        >
            {plusButton}
        </Dropdown>
    )

    let wrapped = dropdown

    if (emoji) {
        wrapped = (
            <Popover
                open={emojiOpen}
                onOpenChange={setEmojiOpen}
                trigger={[]}
                placement={placement}
                classNames={{
                    root: styles.emojiPopover,
                }}
                getPopupContainer={() => document.body}
                content={
                    <div onMouseDown={(e) => e.stopPropagation()}>
                        <Picker
                            data={emojiData}
                            onEmojiSelect={handleEmojiSelect}
                            theme="light"
                            previewPosition="bottom"
                            skinTonePosition="search"
                            autoFocus
                        />
                    </div>
                }
                destroyOnHidden
            >
                <span ref={emojiAnchorRef}>{wrapped}</span>
            </Popover>
        )
    }

    if (showLinkPopover && link && setLinkPopup) {
        wrapped = (
            <Popover
                open={linkPopup.open}
                onOpenChange={(open) => {
                    if (!open) closeLinkPopup()
                }}
                trigger={[]}
                placement={placement}
                overlayClassName={styles.linkPopover}
                getPopupContainer={() => document.body}
                content={linkPopoverContent}
                destroyOnHidden
            >
                {wrapped}
            </Popover>
        )
    }

    return (
        <>
            <span className={wrapClassName}>{wrapped}</span>
            {image?.inputRef && (
                <input
                    ref={image.inputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml,image/bmp,image/x-icon,.png,.jpg,.jpeg,.gif,.webp,.svg,.bmp,.ico"
                    style={{ display: 'none' }}
                    onChange={image.onChange}
                />
            )}
        </>
    )
}

export default QuillInsertDropdown
